import Spinner from '@rio-cloud/rio-uikit/Spinner';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import { useGetScopedBookingsQuery } from '#api/order-communication-booking/booking.slice.ts';
import { knownBookingTypes, type ScopedBookings } from '#api/order-communication-booking/model/scopedBookings.ts';
import { useGetActiveTourServiceTourQuery } from '#api/tourservice/tourService.slice.ts';
import ErrorFallback from '#components/error/ErrorFallback.tsx';
import { NoDataView } from '#components/shared/NoDataView.tsx';
import { TRACKING_CATEGORIES } from '#configuration/setup/googleAnalytics.ts';
import { WidgetUpsellingView } from '#features/livemonitor/upselling/WidgetUpsellingView.tsx';
import { WidgetTourView } from '#features/livemonitor/WidgetTourView.tsx';
import { useFetchDrivers } from '#hooks/useFetchDrivers.ts';
import { NoActiveTourView } from '#pages/NoActiveToureView.tsx';
import { useAppDispatch, useAppSelector } from '#store/store';
import { mapMarkerSelected, selectIsWidgetVisible, selectSelectedAssetId, tourSelected } from '#store/widgetSlice';

export const LivemonitorWidgetPage = () => {
    const selectedAssetId = useAppSelector(selectSelectedAssetId);
    const isWidgetVisible = useAppSelector(selectIsWidgetVisible);

    const intl = useIntl();
    const dispatch = useAppDispatch();

    const {
        data: scopedBookings,
        isLoading: isLoadingScopedBookings,
        error: scopedBookingsError,
    } = useGetScopedBookingsQuery(
        { asset_id: selectedAssetId ?? '' },
        {
            skip: !selectedAssetId,
        }
    );
    const booking = mapScopedBookings(scopedBookings);
    const hasBooking = booking === 'order-communication' || booking === 'pocket-driver';
    const {
        data,
        isLoading: isLoadingTour,
        error: tourError,
    } = useGetActiveTourServiceTourQuery(
        { asset_id: selectedAssetId ?? '' },
        {
            skip: !isWidgetVisible || !selectedAssetId || !hasBooking,
            pollingInterval: 30000,
            skipPollingIfUnfocused: true,
        }
    );
    const { drivers } = useFetchDrivers();

    const selectedTour = data?.tour;
    const selectedDriver = drivers.find(driver => driver.id === selectedTour?.driverId);

    useEffect(() => {
        if (isLoadingScopedBookings) {
            return;
        }
        if (hasBooking) {
            dispatch(tourSelected(selectedTour));
        } else {
            dispatch(tourSelected(undefined));
            dispatch(mapMarkerSelected(undefined));
        }
    }, [isLoadingScopedBookings, hasBooking, selectedTour]);

    if (!selectedAssetId) {
        return (
            <NoDataView
                title={intl.formatMessage({ id: 'intl-msg:ordercom-cloud-frontend.common.no-data-view.title' })}
                message={intl.formatMessage({
                    id: 'intl-msg:order-communication-web.livemonitor.no-data-view.no-asset-label',
                })}
                trackingCategory={TRACKING_CATEGORIES.LIVEMONITOR}
                trackingLabel={'Show no asset selected view'}
            />
        );
    }

    if (scopedBookingsError) {
        console.error('Error fetching scoped bookings:', scopedBookingsError);
        return <ErrorFallback trackingCategory={TRACKING_CATEGORIES.LIVEMONITOR} />;
    }

    if (isLoadingTour || isLoadingScopedBookings) {
        return <Spinner />;
    }

    if (booking === 'none') {
        return <WidgetUpsellingView />;
    }

    if (tourError) {
        console.error('Error fetching active tour:', tourError);
        return <ErrorFallback trackingCategory={TRACKING_CATEGORIES.LIVEMONITOR} />;
    }

    if (!selectedTour) {
        return <NoActiveTourView canOpenTourInOrderCommunication={booking === 'order-communication'} />;
    }

    return (
        <WidgetTourView
            selectedTour={selectedTour}
            driver={selectedDriver}
            canOpenTourInOrderCommunication={booking === 'order-communication'}
        />
    );
};

const mapScopedBookings = (scopedBookings: ScopedBookings | undefined) => {
    if (!scopedBookings) {
        return 'none';
    }
    if (scopedBookings.assetBookings.includes(knownBookingTypes.ORDER_COM_RIO_FMS)) {
        return 'order-communication';
    }
    if (
        scopedBookings.accountBookings.includes(knownBookingTypes.POCKET_DRIVER_VEHICLE) ||
        scopedBookings.accountBookings.includes(knownBookingTypes.POCKET_DRIVER_USER)
    ) {
        return 'pocket-driver';
    }
    if (scopedBookings.accountBookings.includes(knownBookingTypes.ORDER_COM_RIO_FMS)) {
        return 'order-communication-asset-not-booked';
    }
    return 'none';
};
